import { useEffect, useRef } from "react";
import type { LogLineDto, LogLevel } from "../../api/client";
import { cn } from "@/lib/utils";

interface LogTailProps {
  lines: LogLineDto[];
  /** Auto-scroll to bottom as new lines arrive. False while user has paused. */
  autoScroll: boolean;
}

const LEVEL_COLOR: Record<LogLevel, string> = {
  TRACE: "text-dim",
  DEBUG: "text-muted-foreground",
  INFO: "text-info",
  WARN: "text-warning",
  ERROR: "text-sell",
};

/**
 * Read-only scrolling tail. Renders lines in chronological order with severity
 * colour coding. {@code autoScroll} is the backpressure knob — paused freezes
 * the rendered list while the buffer keeps filling.
 */
export default function LogTail({ lines, autoScroll }: LogTailProps) {
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (autoScroll) {
      bottomRef.current?.scrollIntoView({ block: "end" });
    }
  }, [lines, autoScroll]);

  return (
    <div
      className="log-tail bg-[#050810] max-h-[60vh] overflow-auto p-3 font-mono text-xs shadow-[inset_0_2px_18px_-6px_rgba(0,0,0,0.9)]"
      data-testid="log-tail"
    >
      {lines.length === 0 ? (
        <p className="log-tail__empty text-muted-foreground p-2">
          No log lines match the current filters.
        </p>
      ) : (
        <ol className="log-tail__list flex flex-col">
          {lines.map((l, i) => (
            <li
              key={`${l.timeMs}-${i}`}
              className={cn(
                "log-tail__row hover:bg-surface-2/60 flex gap-2 rounded px-1 py-0.5",
                `log-tail__row--${l.level.toLowerCase()}`,
              )}
            >
              <time className="log-tail__ts text-dim shrink-0 tabular-nums">
                {formatTime(l.timeMs)}
              </time>
              <span
                className={cn(
                  "log-tail__level w-12 shrink-0 font-semibold",
                  `log-tail__level--${l.level.toLowerCase()}`,
                  LEVEL_COLOR[l.level] ?? "text-muted-foreground",
                )}
              >
                {l.level}
              </span>
              <code className="log-tail__logger text-muted-foreground shrink-0">
                {shortLogger(l.logger)}
              </code>
              <span className="log-tail__msg text-foreground break-all">
                {l.message}
              </span>
            </li>
          ))}
        </ol>
      )}
      <div ref={bottomRef} />
    </div>
  );
}

function formatTime(ms: number): string {
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return String(ms);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(
    Math.floor(d.getMilliseconds() / 10),
  )}`;
}

/** com.dax.bot.algo.AlgoEngine → AlgoEngine. */
function shortLogger(name: string | null | undefined): string {
  if (!name) return "";
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.substring(dot + 1) : name;
}
